import React, { useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** data-testid for testing/automation */
  'data-testid'?: string;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecione...',
  disabled = false,
  className = '',
  id,
  'data-testid': dataTestId
}) => {
  const selectRef = useRef<HTMLSelectElement>(null);

  // Listen for custom automation events to set value programmatically
  useEffect(() => {
    const select = selectRef.current;
    if (!select) return;

    const handleAutomationSetValue = (event: Event) => {
      const customEvent = event as CustomEvent<{ value: string }>;
      if (customEvent.detail?.value !== undefined) {
        onChange(customEvent.detail.value);
      }
    };

    select.addEventListener('automation:setValue', handleAutomationSetValue);
    return () => {
      select.removeEventListener('automation:setValue', handleAutomationSetValue);
    };
  }, [onChange]);

  return (
    <div className="relative">
      <select
        ref={selectRef}
        id={id}
        data-testid={dataTestId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 pr-10
          bg-white/5 border border-white/30 rounded-lg
          text-foreground-primary
          focus:border-blue-400 focus:ring-2 focus:ring-blue-500/30 focus:outline-none
          hover:border-white/40
          disabled:opacity-50 disabled:cursor-not-allowed
          appearance-none cursor-pointer
          transition-all duration-200
          backdrop-blur-sm
          ${className}
        `}
      >
        {placeholder && (
          <option value="" disabled className="bg-gray-800 text-gray-400">
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-gray-800 text-white">
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Custom chevron icon */}
      <ChevronDown 
        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-foreground-tertiary pointer-events-none" 
      />
    </div>
  );
};